import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  Drawer,
  Typography,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Avatar,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import DivineOrbIcon from '../icons/DivineOrbIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import { convertDDSToWebp } from '../../utils/imageUtils';
import type { Item } from '../../store/slices/itemsSlice';
import { deleteCharacter } from '../../store/slices/charactersSlice';
import { useAppDispatch } from '../../store/hooks.ts';
import { showNotification } from '../../store/slices/notificationSlice';
import type { DivineOrbHistoryDto, DivineOrbPerHourDto } from '../../services/itemService';
import { computeDivHrFromHistory } from '../../utils/divHr';
import ItemDetailsPanel from '../item/ItemDetailsPanel';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role='tabpanel' hidden={value !== index} id={`character-tabpanel-${index}`} aria-labelledby={`character-tab-${index}`} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface CharacterPanelProps {
  open: boolean;
  onClose: () => void;
  character: any | null;
  loading: boolean;
  items: Item[];
  divineOrbHistory?: DivineOrbHistoryDto[];
  divineOrbsPerHour?: DivineOrbPerHourDto;
}

const CharacterPanel: React.FC<CharacterPanelProps> = ({
  open,
  onClose,
  character,
  loading,
  items,
  divineOrbHistory,
  divineOrbsPerHour,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [detailsPanelOpen, setDetailsPanelOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useAppDispatch();
  //const userFeatures = useAppSelector(selectUserFeatures);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handler for opening the item details panel
  const handleItemClick = (item: Item) => {
    setSelectedItem(item);
    setDetailsPanelOpen(true);
  };

  // Handler for closing the item details panel
  const handleCloseDetailsPanel = () => {
    setDetailsPanelOpen(false);
  };

  // Filter items based on search term
  const filteredItems = items?.filter(item => item.name.toLowerCase().includes(searchTerm.toLowerCase())) || [];

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  // Clear search term
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  // Handle delete character
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    if (character && !isDeleting) {
      setIsDeleting(true);
      try {
        await dispatch(deleteCharacter(character.id)).unwrap();
        setDeleteDialogOpen(false);
        onClose(); // Close the character panel after successful deletion
        dispatch(
          showNotification({
            message: `Character "${character.name}" has been successfully deleted.`,
            type: 'success',
          }),
        );
      } catch (error: any) {
        console.error('Failed to delete character:', error);
        dispatch(
          showNotification({
            message: error || 'Failed to delete character. Please try again.',
            type: 'error',
          }),
        );
      } finally {
        setIsDeleting(false);
      }
    }
  };

  if (!character) {
    return null;
  }

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: '600px', md: '700px' },
          maxWidth: '100%',
          padding: 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant='h5' component='h2'>
          {character.name}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handleDeleteClick} aria-label='delete character' color='error'>
            <DeleteIcon />
          </IconButton>
          <IconButton onClick={onClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography variant='body1'>
          <strong>Account:</strong> {character.gameAccount ? character.gameAccount.name : 'Unknown Account'}
        </Typography>
        <Typography variant='body1'>
          <strong>Level:</strong> {character.level}
        </Typography>
        <Typography variant='body1'>
          <strong>Game:</strong> {character.game}
        </Typography>
        <Typography variant='body1'>
          <strong>League:</strong> {character.league}
        </Typography>
        <Typography variant='body1' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <strong>Divine Orbs:</strong>
          <DivineOrbIcon size={16} />
          {character.divineOrbCount ?? 0}
        </Typography>
        <Typography variant='body1' sx={{ whiteSpace: 'pre-line' }}>
          <strong>State:</strong> {character.state || 'Unknown'}
        </Typography>
        <Typography variant='body1'>
          <strong>Hostname:</strong> {character.hostname || 'Unknown'}
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label='character tabs' variant='fullWidth'>
          <Tab label='Inventory' id='character-tab-0' aria-controls='character-tabpanel-0' />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DivineOrbIcon size={16} />
                <span>Divine Orb Analytics</span>
              </Box>
            }
            id='character-tab-1'
            aria-controls='character-tabpanel-1'
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Items ({items ? items.length : 0})
            </Typography>

            {/* Search bar */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                placeholder='Search items by name...'
                value={searchTerm}
                onChange={handleSearchChange}
                variant='outlined'
                size='small'
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm ? (
                    <InputAdornment position='end'>
                      <IconButton aria-label='clear search' onClick={handleClearSearch} edge='end' size='small'>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
            </Box>

            {!items || !Array.isArray(items) || items.length === 0 ? (
              <Typography variant='body1'>No items found for this character.</Typography>
            ) : (
              <List>
                {filteredItems.length === 0 ? (
                  <Typography variant='body1' sx={{ py: 2, textAlign: 'center' }}>
                    No items found matching your search.
                  </Typography>
                ) : (
                  filteredItems.map((item, index) => (
                    <React.Fragment key={item.id || index}>
                      <ListItem
                        onClick={() => handleItemClick(item)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                          }}
                        >
                          {/* Item Icon */}
                          <Avatar
                            src={convertDDSToWebp(item.properties?.ddsPath, item.game)}
                            alt={item.name}
                            variant='square'
                            sx={{ width: 32, height: 32, mr: 2 }}
                          />

                          {/* Item Details */}
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                }}
                              >
                                <Typography variant='body1'>{item.name || 'Unnamed Item'}</Typography>
                                {item.quantity > 1 && <Chip size='small' label={`x${item.quantity}`} />}
                              </Box>
                            }
                            secondary={
                              item.item_type === item.name ? null : (
                                <Typography variant='body2' color='text.secondary'>
                                  {item.item_type || 'Unknown Type'}
                                </Typography>
                              )
                            }
                          />
                        </Box>
                      </ListItem>
                      <Divider component='li' />
                    </React.Fragment>
                  ))
                )}
              </List>
            )}
          </>
        )}
      </TabPanel>

      {/* Divine Orb Analytics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ py: 2 }}>
          <Typography variant='h6' gutterBottom>
            Divine Orb Analytics
          </Typography>

          {/* Div/Hr Stats */}
          <Paper sx={{ p: 2, mb: 3 }} elevation={1}>
            <Typography variant='subtitle1' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DivineOrbIcon size={20} />
              Divine Orbs Per Hour
            </Typography>

            {divineOrbsPerHour ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant='body2' color='text.secondary'>
                    Div/Hr Rate
                  </Typography>
                  <Typography variant='h4' color='primary'>
                    {divineOrbsPerHour.divPerHour.toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant='body2' color='text.secondary'>
                    Net Gain
                  </Typography>
                  <Typography variant='h4'>{divineOrbsPerHour.netGain}</Typography>
                </Box>
                <Box>
                  <Typography variant='body2' color='text.secondary'>
                    Period
                  </Typography>
                  <Typography>{divineOrbsPerHour.hoursElapsed.toFixed(1)} hours</Typography>
                </Box>
                <Box>
                  <Typography variant='body2' color='text.secondary'>
                    Calculation Start
                  </Typography>
                  <Typography>{new Date(divineOrbsPerHour.startTimestamp).toLocaleString()}</Typography>
                </Box>
              </Box>
            ) : (
              <Typography>No Div/Hr data available.</Typography>
            )}
          </Paper>

          {/* 24-Hour History Chart */}
          <Paper sx={{ p: 2 }} elevation={1}>
            <Typography variant='subtitle1' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DivineOrbIcon size={20} />
              24-Hour Divine Orb History
            </Typography>

            {divineOrbHistory && divineOrbHistory.length > 0 ? (
              <Box sx={{ height: 300, mt: 2 }}>
                <ResponsiveContainer width='100%' height='100%'>
                  <LineChart data={divineOrbHistory} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray='3 3' />
                    <XAxis dataKey='timestamp' tickFormatter={timestamp => new Date(timestamp).toLocaleTimeString()} />
                    <YAxis />
                    <Tooltip
                      labelFormatter={timestamp => new Date(timestamp).toLocaleString()}
                      formatter={value => [`${value} divines`, 'Quantity']}
                    />
                    <Line type='monotone' dataKey='quantity' stroke='#8884d8' activeDot={{ r: 8 }} name='Divine Orbs' />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography>No history data available.</Typography>
            )}
          </Paper>

          {/* Div/Hr Rate History Chart */}
          <Paper sx={{ p: 2, mt: 3 }} elevation={1}>
            <Typography variant='subtitle1' gutterBottom>
              24-Hour Div/Hr Rate History
            </Typography>

            {(() => {
              // Compute div/hr rates from divine orb history
              const divRateHistory = divineOrbHistory
                ? computeDivHrFromHistory(
                    divineOrbHistory.map(h => ({
                      timestamp: new Date(h.timestamp),
                      cumulativeDivineOrbs: h.quantity,
                    })),
                  )
                : [];

              return divRateHistory.length > 0 ? (
                <Box sx={{ height: 300, mt: 2 }}>
                  <ResponsiveContainer width='100%' height='100%'>
                    <LineChart data={divRateHistory} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray='3 3' />
                      <XAxis
                        dataKey='timestamp'
                        tickFormatter={timestamp =>
                          new Date(timestamp).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })
                        }
                      />
                      <YAxis />
                      <Tooltip
                        labelFormatter={timestamp => new Date(timestamp).toLocaleString()}
                        formatter={(value, name) => {
                          if (name === 'divPerHour') {
                            return [`${value} Div/Hr`, 'Rate'];
                          }
                          return [`${value}`, name];
                        }}
                      />
                      <Line type='monotone' dataKey='divPerHour' stroke='#ff7300' activeDot={{ r: 8 }} name='divPerHour' />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography>No Div/Hr rate history data available.</Typography>
              );
            })()}
          </Paper>
        </Box>
      </TabPanel>

      {/* Item Details Panel */}
      <ItemDetailsPanel open={detailsPanelOpen} onClose={handleCloseDetailsPanel} item={selectedItem} characterId={character.id} />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby='delete-character-dialog-title'
        aria-describedby='delete-character-dialog-description'
      >
        <DialogTitle id='delete-character-dialog-title'>Delete Character</DialogTitle>
        <DialogContent>
          <Typography id='delete-character-dialog-description'>
            Are you sure you want to delete <strong>{character.name}</strong>?
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            This action cannot be undone. All character data, including item history and events, will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color='error'
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default CharacterPanel;
